import React, { useContext, useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import NeoButton from "./NeoButton";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { MainContext } from "../App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { updateDoc, doc } from "firebase/firestore";
import { db } from "../@shared/firebase/config";

import {
  ConnectComponentsProvider,
  ConnectPayoutsList,
  ConnectBalances,
} from "@stripe/react-connect-js";
import { loadConnectAndInitialize } from "@stripe/connect-js";

const screenHeight = window.innerHeight;
const modalHeight = screenHeight * 0.54;
const modalWidth = modalHeight * (4 / 5);
const backgroundImage = require("../@shared/assets/game_menu.png");

const StripeEarningsModal = ({ visible, onClose }) => {
  const { userData } = useAuthStore();
  const { Theme } = useContext(MainContext);
  const navigate = useNavigate();

  const [stripeConnectInstance, setStripeConnectInstance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initError, setInitError] = useState("");
  const [role, setRole] = useState(null);

  // Load Stripe Connect ONLY when modal is open
  useEffect(() => {
    if (!visible || (!userData?.stripeId && !userData?.stripeTestId)) return;

    const fetchClientSecret = async () => {
      try {
        const response = await axios.post(
          "https://us-central1-infinft-card-game.cloudfunctions.net/app/account_session",
          { stripeId: userData.stripeTestId, test: true }
        );

        if (response.status !== 200) {
          console.error("Bad response from server:", response.data);
          return undefined;
        }

        return response.data.client_secret;
      } catch (error) {
        console.error("Failed to fetch client secret:", error);
        setInitError("Unable to fetch Stripe client secret.");
        return undefined;
      }
    };

    const initializeStripe = async () => {
      setLoading(true);
      try {
        const instance = await loadConnectAndInitialize({
          publishableKey: process.env.REACT_APP_STRIPE_TEST_KEY2,
          fetchClientSecret,
          appearance: {
            overlays: "dialog",
            variables: {
              colorPrimary: Theme.palette.primary.main, // your accent color
              colorText: Theme.palette.secondary.main, // ✨ primary text color
              colorSecondaryText: "#ffffff", // ✨ secondary text color
              buttonPrimaryColorBackground: Theme.palette.primary.main, // ✨ button background color
              buttonPrimaryColorText: "#ffffff", // ✨ button text color

              fontFamily: "Arial, sans-serif", // custom font (limited support)
            },
          },
        });

        setStripeConnectInstance(instance);
      } catch (err) {
        console.error("Stripe Connect initialization error:", err);
        setInitError("Failed to initialize Stripe dashboard.");
      } finally {
        setLoading(false);
      }
    };

    initializeStripe();
  }, [visible, userData?.stripeId]);

  //   const handleOnboard = async () => {
  //     try {
  //       setLoading(true);
  //       const functions = await import("firebase/functions");
  //       const { getFunctions, httpsCallable } = functions;
  //       const createLink = httpsCallable(
  //         getFunctions(),
  //         "createStripeOnboardingLink"
  //       );

  //       const { data } = await createLink({
  //         origin: window.location.origin,
  //         test: true, // Remove in prod
  //       });

  //       window.location.href = data.url;
  //     } catch (err) {
  //       console.error("Onboarding error:", err);
  //       setInitError("Unable to start onboarding.");
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  const handleStartOnboarding = async () => {
    try {
      setLoading(true);
      //update user data
      let newRoles = [...userData.roles, role];
      await updateDoc(doc(db, "users", userData.id), {
        roles: newRoles,
      });
      navigate("/onboarding");
    } catch (err) {
      console.error("Error updating user data:", err);
    }
  };

  return (
    <Modal
      open={visible}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box sx={styles.modalContainer}>
        <img
          src={backgroundImage}
          alt="Background"
          style={styles.backgroundImage}
        />
        <Box sx={styles.modalContent}>
          <Typography
            variant="h6"
            sx={{
              color: "#fff",
              fontWeight: "bold",
              textAlign: "center",
              mb: 2,
            }}
          >
            Stripe Earnings
          </Typography>

          {!userData?.onboardingComplete ? (
            //   && !userData?.stripeTestId
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "80%",
              }}
            >
              <Typography sx={{ color: "#fff", textAlign: "center", mt: 2 }}>
                You haven’t {userData?.stripeId ? "completed" : "started"}{" "}
                Stripe Onboarding yet. Please complete the onboarding process to
                start earning.
              </Typography>
              {!userData?.roles.includes("ip_provider") &&
                !userData?.roles.includes("collection_creator") && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "start",
                      mb: 2,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={role === "ip_provider"}
                          onChange={() => setRole("ip_provider")}
                          sx={{
                            color: "#fff",
                            "&.Mui-checked": {
                              color: "#6f52ed", // Highlighted when selected
                            },
                          }}
                        />
                      }
                      label="NFT Holder (IP Provider)"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={role === "collection_creator"}
                          onChange={() => setRole("collection_creator")}
                          sx={{
                            color: "#fff",
                            "&.Mui-checked": {
                              color: "#6f52ed", // Highlighted when selected
                            },
                          }}
                        />
                      }
                      label="Collection Creator"
                    />
                  </Box>
                )}
              <NeoButton
                text={loading ? "Loading..." : "Complete Onboarding"}
                onClick={() => handleStartOnboarding()}
                disabled={
                  (loading || !role) &&
                  !userData?.roles.includes("ip_provider") &&
                  !userData?.roles.includes("collection_creator")
                }
                color={Theme.palette.primary.main}
              />
            </Box>
          ) : loading ? (
            <Typography sx={{ color: "#ccc", textAlign: "center" }}>
              Loading Stripe dashboard...
            </Typography>
          ) : stripeConnectInstance ? (
            <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
              <Box
                sx={{
                  height: "95%",
                  overflow: "auto",
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, color: "#fff" }}>
                  Balances & Payouts
                </Typography>
                <ConnectBalances />
                <Box sx={{ mt: 3 }}>
                  <ConnectPayoutsList />
                </Box>
              </Box>
            </ConnectComponentsProvider>
          ) : (
            initError && <Typography color="error">{initError}</Typography>
          )}

          <Box
            sx={{
              position: "absolute",
              bottom: "-20px",
              left: 0,
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <NeoButton
              text="Cancel"
              color={Theme.palette.secondary.main}
              width={49}
              onClick={onClose}
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

const styles = {
  modalContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    p: 2,
    height: { xs: "70%", md: "60%" },
    width: { xs: "95%", md: "30%" },
    position: "absolute",
  },
  backgroundImage: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
  },
  modalContent: {
    width: modalWidth,
    height: modalHeight,
    padding: "5% 8% 12%",
    borderRadius: 16,
    position: "relative",
  },
};

export default StripeEarningsModal;
